import { ApplicationStatus } from '@/apis/family_application/requests'
import { LogUserActivity } from '@/apis/users/requests'
import { buttonVariants } from '@/app/common/components/Button'
import { CardFooter } from '@/app/common/components/Card'
import {
  CartIcon,
  ReportIcon,
  SupportIcon,
  WalletIcon,
} from '@/app/common/icons/general'
import { cn, currencyFormatter } from '@/app/common/utilities'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import { getMenuItems } from '@/layouts/Sidebar/sidebar.constants'
import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/Select.tsx'
import {
  selectFamilyApplication,
  setSelectedStudent,
} from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch, RootState } from '@/storage/store'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { LOG_TYPES } from '../family_application/constants'
import { useStudentPurses } from '../providers/api/usePaymentProviders'

export default function DashboardPage() {
  const dispatch = useDispatch<AppDispatch>()
  const { t: translate } = useTranslation()
  const application = useSelector(selectFamilyApplication)
  const programSettings = useSelector(
    (state: RootState) => state.ProgramSettings
  )
  const programId = useSelector(selectProgramID)
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    application.students[0]?.id ?? undefined
  )
  const { data: purses } = useStudentPurses(
    { program_id: programId, application_id: application?.application_id },
    { enabled: !!application?.application_id && !!programId }
  )

  const addPropsToIcon = (icon: ReactElement, additionalProps: object) => {
    return React.cloneElement(icon, additionalProps)
  }

  if (!programSettings.data) {
    return null
  }

  const applicationStatuses: ApplicationStatus[] = application.students.map(
    (student) => student.application_status
  ) ?? ['In Progress']

  function getInitials(first_name?: string | null, last_name?: string | null) {
    if (first_name && last_name) {
      return `${first_name.slice(0, 1)}${last_name.slice(0, 1)}`
    }
    return ''
  }

  const selectedPurses =
    purses?.filter((purse) => purse.student_id === selectedStudentId) ?? []
  const selectedStudent = application?.students?.find(
    (student) => student.id === selectedStudentId
  )

  return (
    <div className="mt-[-20px]">
      <div className="flex flex-col items-center gap-2 lg:gap-3.5 py-4 lg:pt-5 lg:pb-10">
        <div className="w-20 h-20 bg-primary-100 flex items-center justify-center rounded-full">
          {getInitials(selectedStudent?.first_name, selectedStudent?.last_name)}
        </div>
        <div className="flex items-center">
          <Select
            value={selectedStudentId?.toString()}
            onValueChange={(value) => setSelectedStudentId(Number(value))}
          >
            <SelectTrigger className="w-full gap-2 text-lg font-medium text-gray-900 rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 ">
              <SelectValue placeholder={translate('select_student')} />
            </SelectTrigger>
            <SelectContent>
              {application?.students.map((student) => {
                return (
                  <SelectItem key={student.id} value={student.id.toString()}>
                    {`${student.first_name} ${student.last_name}`}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-7.5 mt-5">
        <div className="col-span-1 w-full col-span-1">
          <Card className="w-full shadow-card">
            <CardHeader className="flex justify-between items-center text-md font-semibold py-4">
              <div className="flex items-center gap-2">
                <WalletIcon className="text-xl text-gray-700" />
                {translate('purse_information')}
              </div>
            </CardHeader>
            <CardContent className="font-medium text-gray-900 w-full space-y-8">
              {selectedPurses.length > 0 ? (
                selectedPurses.map((purse) => {
                  return (
                    <div className="border-b border-gray-200 mb-1 pb-2">
                      <div className="">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-md text-gray-800">
                            {purse.name}
                          </div>
                          <div className="text-gray-800 text-lg">
                            {currencyFormatter.format(purse.total)}
                          </div>
                        </div>
                        <div className="font-normal text-md text-gray-800 w-full pl-2">
                          <div className="border-b border-gray-200 my-2 py-2">
                            {purse.pending_payments.map((payment) => {
                              return (
                                <div className="flex items-center justify-between">
                                  <div className="text-sm">
                                    {payment.provider_name ?? payment.type}
                                  </div>
                                  {currencyFormatter.format(payment.amount)}
                                </div>
                              )
                            })}
                          </div>
                          <div className="flex items-center justify-between w-full">
                            <Label className="font-normal">Total Pending</Label>
                            <div className="">
                              {currencyFormatter.format(purse.balance_frozen)}
                            </div>
                          </div>
                          <div className="flex items-center justify-between w-full">
                            <Label className="font-normal">Purse Balance</Label>
                            <div className="">
                              {currencyFormatter.format(
                                purse.balance_available
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="font-normal text-center">
                  Student has no purses
                </div>
              )}

              {selectedPurses.length > 0 && (
                <div className="text-right">
                  <Link
                    to={`${AppRoutePaths.transactions.index}?filter=byStudent`}
                    className={'text-primary'}
                    onClick={() =>
                      dispatch(setSelectedStudent(selectedStudentId))
                    }
                  >
                    {translate('view_all_transactions')}
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="col-span-1 w-full hidden">
          <Card className="w-full shadow-card">
            <CardHeader className="flex justify-between items-center text-md font-semibold py-4">
              <div className="flex items-center gap-2">
                <CartIcon className="w-6 text-gray-700" />
                {translate('pay_providers')}
              </div>
            </CardHeader>
            <CardContent className="justify-center flex text-2xl font-medium text-gray-900 py-12">
              <Link
                to={AppRoutePaths.providers.pay_provider.index}
                className={cn(
                  buttonVariants({ variant: 'primary' }),
                  'uppercase'
                )}
              >
                {translate('pay_providers')}
              </Link>
            </CardContent>
          </Card>
        </div>
        <div className="col-span-1 w-full hidden">
          <Card className="w-full shadow-card">
            <CardHeader className="flex justify-between items-center text-md font-semibold py-4">
              <div className="flex items-center gap-2">
                <ReportIcon className="text-xl text-gray-700" />
                {translate('reports')}
              </div>
            </CardHeader>
            <CardContent className="">
              <div className="flex items-center justify-between px-5 xl:px-20 mt-3">
                <Link
                  to={'#'}
                  className="cursor-pointer text-primary text-sm font-semibold"
                >
                  {translate('funds_received')}
                </Link>
                <Link
                  to={'#'}
                  className="cursor-pointer text-primary text-sm font-semibold"
                >
                  {translate('orders')}
                </Link>
              </div>
            </CardContent>
            <CardFooter className="flex items-center justify-center border-none mb-3">
              <Link
                to={'#'}
                className="cursor-pointer text-primary text-sm font-semibold"
              >
                {translate('pending_approvals')}
              </Link>
            </CardFooter>
          </Card>
        </div>
        <div className="col-span-1 w-full hidden">
          <Card className="w-full shadow-card">
            <CardHeader className="flex justify-between items-center text-md font-semibold py-4">
              <div className="flex items-center gap-2">
                <SupportIcon className="text-xl text-gray-700" />
                {translate('support')}
              </div>
            </CardHeader>
            <CardContent className="">
              <div className="flex items-center justify-between px-5 xl:px-20 mt-3">
                <Link
                  to={'#'}
                  className="cursor-pointer text-primary text-sm font-semibold"
                >
                  {translate('training_videos')}
                </Link>
                <Link
                  to={'#'}
                  className="cursor-pointer text-primary text-sm font-semibold"
                >
                  {translate('faqs')}
                </Link>
              </div>
            </CardContent>
            <CardFooter className="flex items-center justify-center border-none mb-3">
              <Link
                to={'#'}
                className="cursor-pointer text-primary text-sm font-semibold"
              >
                {translate('contact_us')}
              </Link>
            </CardFooter>
          </Card>
        </div>
        <div className="col-span-1 lg:col-span-2">
          <div className="mx-auto p-0">
            <div className="grid grid-cols-2 xs:grid-cols-1 xl:grid-cols-3 gap-4 xl:gap-6">
              {getMenuItems(
                applicationStatuses,
                programSettings.data,
                application
              )
                .filter((e) => e.title != 'Dashboard')
                .map((item, index) => (
                  <Link
                    to={item.route}
                    target={item.route.startsWith('http') ? `_blank` : '_self'}
                    key={index}
                    onClick={() => {
                      if (['handbook', 'help_center'].includes(item.title))
                        LogUserActivity({
                          log_type: LOG_TYPES.VIEW_PAGE,
                          suffix_text:
                            item.title == 'handbook'
                              ? 'the Handbook'
                              : 'the Help Center',
                        })
                    }}
                  >
                    <div
                      key={index}
                      className="bg-white rounded-lg border shadow py-2 lg:py-6"
                    >
                      <span className="flex w-full items-center justify-center text-gray-400 shrink-0 group-hover:text-primary-600 p-3 h-16 text-2xl">
                        {addPropsToIcon(item.icon, { className: 'text-4xl' })}
                      </span>
                      <div className="mt-2">
                        <p className="text-md md:text-lg text-center font-semibold text-gray-900">
                          {translate(item.title)}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
